import type { ReactNode } from "react";

import { useTranslation } from "@/store/locale";
import { useDelayed } from "@/hooks/use-delayed";
import type { TranslationKey } from "@/constants/i18n";
import { cn } from "@/utils/utils";

/**
 * What is happening, so the message can say it.
 *
 * "Loading…" on a screen where somebody just pressed Search is a worse answer
 * than "Searching members…": the wait is the same, but one of them tells you
 * your action was received.
 */
export type LoadingAction =
  | "loading"
  | "searching"
  | "filtering"
  | "sorting"
  | "paging"
  | "saving"
  | "deleting"
  | "exporting";

const MESSAGES: Record<LoadingAction, TranslationKey> = {
  loading: "load.loading",
  searching: "load.searching",
  filtering: "load.filtering",
  sorting: "load.sorting",
  paging: "load.paging",
  saving: "load.saving",
  deleting: "load.deleting",
  exporting: "load.exporting",
};

/**
 * The announcement that a wait is on — and nothing a pointer can see.
 *
 * There is no spinner and no line of prose. Both sat above the rows and pushed
 * them down every time anyone typed, and the skeleton or the dimmed rows below
 * were already saying the same thing in the place the eye was looking.
 *
 * The sentence is still written — "Searching members…" — into a live region,
 * because a screen reader has no dimmed rows to notice. That is the whole of
 * what this renders, and it takes no space.
 */
export function LoadingNote({
  action = "loading",
  subject,
}: {
  action?: LoadingAction;
  /** What is being acted on: "members", "clubs". Left out for a bare message. */
  subject?: string | undefined;
}) {
  const t = useTranslation();
  const message = t(MESSAGES[action]);
  return (
    <p role="status" className="sr-only">
      {subject
        ? message.replace("{subject}", subject.toLowerCase())
        : message.replace(" {subject}", "")}
    </p>
  );
}

/**
 * A page's own loading state: its skeleton, and the announcement of what for.
 *
 * `children` is the skeleton, and it is the caller's because it has to be the
 * shape of that page — a table's rows, a map's canvas, a form's fields.
 * Nothing generic can hold the right space, and holding the right space is the
 * whole job.
 *
 * Announces nothing until the wait is worth mentioning; see `useDelayed`.
 */
export function Loading({
  busy,
  action = "loading",
  subject,
  children,
  className,
}: {
  busy: boolean;
  action?: LoadingAction;
  subject?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  const show = useDelayed(busy);
  if (!busy) return null;
  return (
    <div aria-busy className={cn("grid gap-3", className)}>
      {show ? <LoadingNote action={action} subject={subject} /> : null}
      {children}
    </div>
  );
}
